import type { Group, GroupOrderStatus, InvoiceStatus } from './schema'

/** Which server-side filter a top-level tab stands for. */
export interface OrderFilter {
  /** Comma-joined for `order_status`; the endpoint accepts a list. */
  orderStatuses: readonly GroupOrderStatus[]
  invoiceStatus?: InvoiceStatus
}

/**
 * The five tabs from the design. `CANCELLED` sits under "Selesai" because the
 * backend counts it there too — `order_status=COMPLETED,CANCELLED` returns exactly
 * the number its `completed_counter` reports.
 */
export const orderTabs = [
  {
    key: 'unpaid',
    label: 'Menunggu Konfirmasi',
    orderStatuses: ['UNPAID'],
    emptyState: 'onboarding',
  },
  {
    key: 'billed',
    label: 'Menunggu Pembayaran',
    orderStatuses: ['BILLED'],
  },
  { key: 'processing', label: 'Diproses', orderStatuses: ['IN_PROCESS'], emptyState: 'readyToShip' },
  { key: 'shipped', label: 'Dikirim', orderStatuses: ['SHIPPED'], emptyState: 'shipped' },
  { key: 'done', label: 'Selesai', orderStatuses: ['COMPLETED', 'CANCELLED'], emptyState: 'done' },
] as const satisfies ReadonlyArray<{
  key: string
  label: string
  orderStatuses: readonly GroupOrderStatus[]
  emptyState?: EmptyStateVariant
}>

export type EmptyStateVariant = 'onboarding' | 'billed' | 'readyToShip' | 'shipped' | 'done'
export type OrderTabKey = (typeof orderTabs)[number]['key']
/** Kept for old detail-group URLs and per-order billed action rules. */
export type OrderSubTabKey = 'all' | 'awaiting-payment'

/** Tabs that load orders in pages of {@link ORDER_PAGE_SIZE} with infinite scroll. */
export const paginatedOrderTabs = [
  'unpaid',
  'billed',
  'processing',
  'shipped',
  'done',
] as const satisfies readonly OrderTabKey[]

export type PaginatedOrderTabKey = (typeof paginatedOrderTabs)[number]

export const ORDER_PAGE_SIZE = 10

/** Tab badge label; counts above 99 render as `99+`. */
export function formatOrderTabCount(count: number): string {
  return count > 99 ? '99+' : String(count)
}

/** Hide tab count badges while the order search query is in use (WEBLP-2514). */
export function shouldHideOrderTabCounts(search: string): boolean {
  return search.trim() !== ''
}

export function isPaginatedOrderTab(tab: OrderTabKey): tab is PaginatedOrderTabKey {
  return (paginatedOrderTabs as readonly OrderTabKey[]).includes(tab)
}

export interface OrderTab {
  key: OrderTabKey
  label: string
  orderStatuses: readonly GroupOrderStatus[]
  emptyState?: EmptyStateVariant
}

export const orderStatusLabels: Record<GroupOrderStatus, string> = {
  UNPAID: 'Menunggu Konfirmasi',
  BILLED: 'Menunggu Pembayaran',
  IN_PROCESS: 'Diproses',
  SHIPPED: 'Dikirim',
  COMPLETED: 'Selesai',
  CANCELLED: 'Dibatalkan',
  UNKNOWN: 'Lainnya',
}

/** Widened to the interface for consistent tab access. */
export function findTab(key: OrderTabKey): OrderTab {
  return orderTabs.find((tab) => tab.key === key)!
}

/** The server-side order filter for a top-level tab. */
export function filterFor(tabKey: OrderTabKey): OrderFilter {
  return {
    orderStatuses: findTab(tabKey).orderStatuses,
  }
}

/** Resolve billed-row actions from invoice status, independent of the legacy URL sub-tab. */
export function subTabForOrder(
  tabKey: OrderTabKey,
  subTabKey: OrderSubTabKey,
  invoiceStatus: InvoiceStatus | null,
) {
  if (tabKey !== 'billed') return subTabKey
  return invoiceStatus === 'PENDING' ? 'awaiting-payment' : 'all'
}

export function emptyStateFor(tabKey: OrderTabKey): EmptyStateVariant {
  return findTab(tabKey).emptyState ?? 'onboarding'
}

/** Maps the active tab to its badge count key. */
export function activeBadgeCountKey(tabKey: OrderTabKey) {
  return tabKey
}

/** Every filter whose count a badge displays. */
export const badgeFilters: ReadonlyArray<OrderFilter & { key: string }> = [
  ...orderTabs.map((tab) => ({ key: tab.key as string, orderStatuses: tab.orderStatuses })),
]

export function serializeOrderStatuses(statuses: readonly GroupOrderStatus[]) {
  return statuses.join(',')
}

/**
 * Badge numbers from the group-list counters. Used when the order endpoint
 * 404s (unverified groups) so the tabs still match the dashboard row.
 */
export function countsFromGroupList(group: Group): Record<string, number> {
  return {
    unpaid: group.buckets.new.count,
    billed: group.buckets.billed.count,
    processing: group.buckets.processing.count,
    shipped: group.buckets.shipped.count,
    done: group.completedCount,
  }
}

interface BuildBadgeCountsInput {
  listed: Group | undefined
  /** When set, skip group-list counters — only the active tab can be sourced from the list. */
  search: string
  activeBadgeKey: string
  activeBadgeCount: number | undefined
  ordersUnavailable: boolean
}

/**
 * Badge counts without fan-out `per_page=1` requests on page load.
 * Non-active tabs use group-list counters; the open tab uses list pagination.
 */
export function buildBadgeCounts({
  listed,
  search,
  activeBadgeKey,
  activeBadgeCount,
  ordersUnavailable,
}: BuildBadgeCountsInput) {
  const base =
    listed && (ordersUnavailable || search === '')
      ? countsFromGroupList(listed)
      : {}

  const counts: Record<string, number | undefined> = { ...base }

  if (activeBadgeCount != null) {
    counts[activeBadgeKey] = activeBadgeCount
  }

  return counts
}
